"use client";

import { Modal } from "@pamperedchef/design-system";
import { useCallback, useEffect, useRef, useState } from "react";

import {
	BLOOMREACH_WEBLAYER_SELECTOR,
	isBloomreachWebLayer,
	removeBloomreachWebLayer,
	showLeadCaptureBanner,
	type LeadCaptureParams
} from "@/app/utilities/analytics";

const SDK_TIMEOUT_MS = 5000;
const SHOW_BANNER_DELAY_MS = 100;

export interface UseBloomreachBannerReturn {
	isOpen: boolean;
	open: () => void;
	close: () => void;
}

/**
 * Hook that manages the Bloomreach lead capture banner lifecycle.
 *
 * Handles opening/closing state, calling `showBanner` via the Bloomreach SDK,
 * observing the DOM for weblayer removal (form submit or user close),
 * and cleaning up the weblayer when the caller closes programmatically.
 *
 * @example
 * const banner = useBloomreachBanner({ country: "US", consultantId: "123" });
 * <Button onPress={banner.open}>Subscribe</Button>
 * <BloomreachLeadCapture {...banner} />
 */
export function useBloomreachBanner(
	params: LeadCaptureParams
): UseBloomreachBannerReturn {
	const [isOpen, setIsOpen] = useState(false);
	const observerRef = useRef<MutationObserver | null>(null);

	const open = useCallback(() => {
		if (typeof window === "undefined" || !window.exponea) return;
		setIsOpen(true);
	}, []);

	const close = useCallback(() => {
		removeBloomreachWebLayer();
		setIsOpen(false);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			observerRef.current?.disconnect();
			observerRef.current = null;
			return;
		}

		const showBannerTimer = setTimeout(() => {
			showLeadCaptureBanner(params);

			const observer = new MutationObserver((mutations) => {
				for (const mutation of mutations) {
					const removed = Array.from(mutation.removedNodes);
					for (const node of removed) {
						if (
							node instanceof HTMLElement &&
							isBloomreachWebLayer(node)
						) {
							setIsOpen(false);
							observer.disconnect();
							return;
						}
					}
				}
			});
			observer.observe(document.body, {
				childList: true,
				subtree: true
			});
			observerRef.current = observer;
		}, SHOW_BANNER_DELAY_MS);

		const safetyTimer = setTimeout(() => {
			const weblayer = document.querySelector(
				BLOOMREACH_WEBLAYER_SELECTOR
			);
			if (!weblayer) {
				setIsOpen(false);
			}
		}, SDK_TIMEOUT_MS);

		return () => {
			clearTimeout(showBannerTimer);
			clearTimeout(safetyTimer);
			observerRef.current?.disconnect();
		};
	}, [isOpen, params]);

	return { isOpen, open, close };
}

export interface BloomreachLeadCaptureProps extends UseBloomreachBannerReturn {
	/** Accessible label for the modal overlay */
	ariaLabel?: string;
}

/**
 * Reusable Bloomreach lead capture modal.
 *
 * Renders a design-system Modal that acts as the backdrop and accessibility
 * container while the Bloomreach SDK renders its own form overlay on top.
 *
 * @example
 * import { useBloomreachBanner, BloomreachLeadCapture } from "@/app/components/shared/BloomreachLeadCapture";
 *
 * const banner = useBloomreachBanner({ country: "US" });
 * return (
 *   <>
 *     <Button onPress={banner.open}>Subscribe</Button>
 *     <BloomreachLeadCapture {...banner} />
 *   </>
 * );
 */
export function BloomreachLeadCapture({
	isOpen,
	close,
	ariaLabel = "Newsletter Sign Up"
}: BloomreachLeadCaptureProps): JSX.Element {
	return (
		<Modal
			isOpen={isOpen}
			onClose={close}
			onClosePress={close}
			hideCloseButton
			className="bloomreach-modal"
			overlayClassName="bloomreach-modal-overlay"
			aria-label={ariaLabel}
		>
			<div />
		</Modal>
	);
}
